import React, { useState, Fragment, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import {
  AlarmClock,
  AlertCircle,
  AlertTriangle,
  ArrowUpDown,
  Clock,
  Eye,
  EyeOff,
  Fingerprint,
  Globe,
  Key,
  LockKeyhole,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  ShieldOff,
  Trash2,
  User,
  UserX,
  X,
} from 'lucide-react';
import { format } from 'date-fns';

// Define types
interface SecuritySettings {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    passwordExpiryDays: number;
    preventPasswordReuse: boolean;
  };
  mfaSettings: {
    enforced: boolean;
    allowedMethods: string[];
    graceLoginCount: number;
  };
  sessionSettings: {
    sessionTimeout: number; // minutes
    enforceSingleSession: boolean;
    inactivityTimeout: number; // minutes
  };
  loginProtection: {
    maxLoginAttempts: number;
    lockoutDuration: number; // minutes
    ipRateLimiting: boolean;
    requireCaptcha: boolean;
  };
}

interface BlockedIP {
  id: string;
  ipAddress: string;
  reason: string;
  blockedAt: string;
  blockedUntil: string | null;
  attemptCount: number;
}

interface BlockedUser {
  id: number;
  username: string;
  email: string;
  reason: string;
  blockedAt: string;
  blockedUntil: string | null;
  attemptCount: number;
}

interface SecurityEvent {
  id: string;
  timestamp: string;
  eventType: string;
  ipAddress: string;
  userAgent: string;
  userId: number | null;
  username: string | null;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

// Default security settings
const defaultSecuritySettings: SecuritySettings = {
  passwordPolicy: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    passwordExpiryDays: 90,
    preventPasswordReuse: true,
  },
  mfaSettings: {
    enforced: false,
    allowedMethods: ['totp', 'sms', 'email'],
    graceLoginCount: 3,
  },
  sessionSettings: {
    sessionTimeout: 60,
    enforceSingleSession: false,
    inactivityTimeout: 30,
  },
  loginProtection: {
    maxLoginAttempts: 5,
    lockoutDuration: 30,
    ipRateLimiting: true,
    requireCaptcha: false,
  },
};

// Statistics
interface SecurityStats {
  loginAttempts: {
    total: number;
    successful: number;
    failed: number;
  };
  accountLockouts: number;
  mfaAdoption: number;
  securityEvents: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  activeUsers: number;
  activeAdmins: number;
  blockedIPs: number;
  blockedUsers: number;
}

const defaultSecurityStats: SecurityStats = {
  loginAttempts: {
    total: 0,
    successful: 0,
    failed: 0,
  },
  accountLockouts: 0,
  mfaAdoption: 0,
  securityEvents: {
    low: 0,
    medium: 0,
    high: 0,
    critical: 0,
  },
  activeUsers: 0,
  activeAdmins: 0,
  blockedIPs: 0,
  blockedUsers: 0,
};

const SecurityPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch security settings
  const { data: securitySettings = defaultSecuritySettings, isLoading: settingsLoading } = useQuery<SecuritySettings>({
    queryKey: ['/api/admin/system/security/settings'],
    queryFn: async () => {
      try {
        // apiRequest already returns parsed JSON data
        const response = await apiRequest('/api/admin/system/security/settings', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultSecuritySettings;
      }
    },
  });

  // Fetch security stats
  const { data: securityStats = defaultSecurityStats, isLoading: statsLoading } = useQuery<SecurityStats>({
    queryKey: ['/api/admin/system/security/stats'],
    queryFn: async () => {
      try {
        // apiRequest already returns parsed JSON data
        const response = await apiRequest('/api/admin/system/security/stats', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultSecurityStats;
      }
    },
  });

  // Fetch blocked IPs
  const { data: blockedIPs = [], isLoading: blockedIPsLoading } = useQuery<BlockedIP[]>({
    queryKey: ['/api/admin/system/security/blocked-ips'],
    queryFn: async () => {
      try {
        // apiRequest already returns parsed JSON data
        const response = await apiRequest('/api/admin/system/security/blocked-ips', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Fetch blocked users
  const { data: blockedUsers = [], isLoading: blockedUsersLoading } = useQuery<BlockedUser[]>({
    queryKey: ['/api/admin/system/security/blocked-users'],
    queryFn: async () => {
      try {
        // apiRequest already returns parsed JSON data
        const response = await apiRequest('/api/admin/system/security/blocked-users', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Fetch security events
  const { data: securityEvents = [], isLoading: eventsLoading } = useQuery<SecurityEvent[]>({
    queryKey: ['/api/admin/system/security/events'],
    queryFn: async () => {
      try {
        // apiRequest already returns parsed JSON data
        const response = await apiRequest('/api/admin/system/security/events', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return [];
      }
    },
  });

  // Update security settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: SecuritySettings) => {
      // apiRequest already returns parsed JSON data
      const response = await apiRequest('/api/admin/system/security/settings', {
        method: 'POST',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/security/settings'] });
      toast({
        title: 'Security settings updated',
        description: 'Security settings have been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating security settings:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update security settings.',
        variant: 'destructive',
      });
    },
  });

  // Unblock IP
  const unblockIPMutation = useMutation({
    mutationFn: async (ipId: string) => {
      // apiRequest already returns parsed JSON data
      const response = await apiRequest(`/api/admin/system/security/blocked-ips/${ipId}/unblock`, {
        method: 'POST',
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/security/blocked-ips'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/security/stats'] });
      toast({
        title: 'IP unblocked',
        description: 'The IP address has been successfully unblocked.',
      });
    },
    onError: (error) => {
      console.error('Error unblocking IP:', error);
      toast({
        title: 'Action failed',
        description: 'Failed to unblock IP address.',
        variant: 'destructive',
      });
    },
  });

  // Unblock user
  const unblockUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      // apiRequest already returns parsed JSON data
      const response = await apiRequest(`/api/admin/system/security/blocked-users/${userId}/unblock`, {
        method: 'POST',
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/security/blocked-users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/security/stats'] });
      toast({
        title: 'User unblocked',
        description: 'The user has been successfully unblocked.',
      });
    },
    onError: (error) => {
      console.error('Error unblocking user:', error);
      toast({
        title: 'Action failed',
        description: 'Failed to unblock user.',
        variant: 'destructive',
      });
    },
  });

  // State for form values with proper default values
  const [editSettings, setEditSettings] = useState<SecuritySettings>(defaultSecuritySettings);

  // Update form state when data loads
  useEffect(() => {
    if (!settingsLoading && securitySettings) {
      setEditSettings({
        passwordPolicy: {
          ...defaultSecuritySettings.passwordPolicy,
          ...securitySettings.passwordPolicy
        },
        mfaSettings: {
          ...defaultSecuritySettings.mfaSettings,
          ...securitySettings.mfaSettings
        },
        sessionSettings: {
          ...defaultSecuritySettings.sessionSettings,
          ...securitySettings.sessionSettings
        },
        loginProtection: {
          ...defaultSecuritySettings.loginProtection,
          ...securitySettings.loginProtection
        }
      });
    }
  }, [settingsLoading, securitySettings]);

  // Format date
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  };

  // Handle form submission
  const handleSettingsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettingsMutation.mutate(editSettings);
  };

  // Get severity badge color
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      case 'high':
        return 'bg-orange-100 text-orange-800 hover:bg-orange-100';
      case 'critical':
        return 'bg-red-100 text-red-800 hover:bg-red-100';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  };

  // Toggle event expansion
  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  // Filter events by search query
  const filteredEvents = searchQuery
    ? securityEvents.filter((event) => {
        return (
          event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.eventType.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.ipAddress.includes(searchQuery) ||
          (event.username && event.username.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      })
    : securityEvents;

  const isLoading = settingsLoading || statsLoading || blockedIPsLoading || blockedUsersLoading || eventsLoading;

  return (
    <div className="min-h-screen bg-slate-50">
      <SuperAdminLayout>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Security Management</h1>
            <p className="text-slate-500">Manage system security settings and view security events</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="overview">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="settings">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="blocked">
              <ShieldOff className="h-4 w-4 mr-2" />
              Blocked
            </TabsTrigger>
            <TabsTrigger value="events">
              <AlertCircle className="h-4 w-4 mr-2" />
              Events
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Security Overview</CardTitle>
                <CardDescription>System-wide security statistics and metrics</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Card>
                        <CardHeader className="p-4 pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">Login Attempts</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                          <div className="flex justify-between items-center">
                            <div className="text-2xl font-bold">{securityStats.loginAttempts.total}</div>
                            <div className="flex flex-col text-right">
                              <span className="text-xs text-green-500">{securityStats.loginAttempts.successful} successful</span>
                              <span className="text-xs text-red-500">{securityStats.loginAttempts.failed} failed</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="p-4 pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">Account Lockouts</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                          <div className="text-2xl font-bold">{securityStats.accountLockouts}</div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="p-4 pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">MFA Adoption</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                          <div className="text-2xl font-bold">{securityStats.mfaAdoption}%</div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="p-4 pb-2">
                          <CardTitle className="text-sm font-medium text-muted-foreground">Active Users</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                          <div className="flex justify-between items-center">
                            <div className="text-2xl font-bold">{securityStats.activeUsers}</div>
                            <div className="text-xs text-muted-foreground">
                              {securityStats.activeAdmins} admins
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <h3 className="font-medium text-lg">Security Events</h3>
                      <Card>
                        <CardContent className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="flex flex-col items-center p-3 bg-blue-50 rounded-lg">
                              <div className="text-blue-500 text-sm font-medium">Low</div>
                              <div className="text-xl font-bold">{securityStats?.securityEvents?.low || 0}</div>
                            </div>
                            <div className="flex flex-col items-center p-3 bg-yellow-50 rounded-lg">
                              <div className="text-yellow-500 text-sm font-medium">Medium</div>
                              <div className="text-xl font-bold">{securityStats?.securityEvents?.medium || 0}</div>
                            </div>
                            <div className="flex flex-col items-center p-3 bg-orange-50 rounded-lg">
                              <div className="text-orange-500 text-sm font-medium">High</div>
                              <div className="text-xl font-bold">{securityStats?.securityEvents?.high || 0}</div>
                            </div>
                            <div className="flex flex-col items-center p-3 bg-red-50 rounded-lg">
                              <div className="text-red-500 text-sm font-medium">Critical</div>
                              <div className="text-xl font-bold">{securityStats?.securityEvents?.critical || 0}</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <h3 className="font-medium text-lg">Blocked IPs</h3>
                        <Card>
                          <CardContent className="p-0">
                            {blockedIPs.length === 0 ? (
                              <div className="flex flex-col items-center justify-center p-8">
                                <Shield className="h-10 w-10 text-muted-foreground opacity-50 mb-2" />
                                <div className="text-muted-foreground">No blocked IP addresses</div>
                              </div>
                            ) : (
                              <div className="rounded-md overflow-hidden">
                                <table className="w-full caption-bottom text-sm">
                                  <thead>
                                    <tr className="border-b bg-slate-50 transition-colors hover:bg-slate-100">
                                      <th className="h-10 px-4 text-left align-middle font-medium">IP Address</th>
                                      <th className="h-10 px-4 text-left align-middle font-medium">Blocked At</th>
                                      <th className="h-10 px-4 text-left align-middle font-medium">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {blockedIPs.slice(0, 5).map((ip) => (
                                      <tr key={ip.id} className="border-b transition-colors hover:bg-slate-100">
                                        <td className="p-2 align-middle">
                                          <div className="font-medium">{ip.ipAddress}</div>
                                          <div className="text-xs text-muted-foreground">{ip.reason}</div>
                                        </td>
                                        <td className="p-2 align-middle text-xs">
                                          {formatDate(ip.blockedAt)}
                                        </td>
                                        <td className="p-2 align-middle">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => unblockIPMutation.mutate(ip.id)}
                                            disabled={unblockIPMutation.isPending}
                                          >
                                            <Shield className="h-4 w-4 mr-1" />
                                            Unblock
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </CardContent>
                          {blockedIPs.length > 5 && (
                            <CardFooter className="py-2">
                              <Button
                                variant="link"
                                size="sm"
                                className="ml-auto"
                                onClick={() => setActiveTab('blocked')}
                              >
                                View all ({blockedIPs.length})
                              </Button>
                            </CardFooter>
                          )}
                        </Card>
                      </div>

                      <div className="space-y-2">
                        <h3 className="font-medium text-lg">Blocked Users</h3>
                        <Card>
                          <CardContent className="p-0">
                            {blockedUsers.length === 0 ? (
                              <div className="flex flex-col items-center justify-center p-8">
                                <User className="h-10 w-10 text-muted-foreground opacity-50 mb-2" />
                                <div className="text-muted-foreground">No blocked users</div>
                              </div>
                            ) : (
                              <div className="rounded-md overflow-hidden">
                                <table className="w-full caption-bottom text-sm">
                                  <thead>
                                    <tr className="border-b bg-slate-50 transition-colors hover:bg-slate-100">
                                      <th className="h-10 px-4 text-left align-middle font-medium">User</th>
                                      <th className="h-10 px-4 text-left align-middle font-medium">Blocked At</th>
                                      <th className="h-10 px-4 text-left align-middle font-medium">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {blockedUsers.slice(0, 5).map((user) => (
                                      <tr key={user.id} className="border-b transition-colors hover:bg-slate-100">
                                        <td className="p-2 align-middle">
                                          <div className="font-medium text-xs truncate max-w-[150px]">{user.email}</div>
                                        </td>
                                        <td className="p-2 align-middle text-xs">
                                          {formatDate(user.blockedAt)}
                                        </td>
                                        <td className="p-2 align-middle">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => unblockUserMutation.mutate(user.id)}
                                            disabled={unblockUserMutation.isPending}
                                          >
                                            <User className="h-4 w-4 mr-1" />
                                            Unblock
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </CardContent>
                          {blockedUsers.length > 5 && (
                            <CardFooter className="py-2">
                              <Button
                                variant="link"
                                size="sm"
                                className="ml-auto"
                                onClick={() => setActiveTab('blocked')}
                              >
                                View all ({blockedUsers.length})
                              </Button>
                            </CardFooter>
                          )}
                        </Card>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Configure system-wide security policies and settings</CardDescription>
              </CardHeader>
              <CardContent>
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <form onSubmit={handleSettingsSubmit} className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Password Policy</h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="password-length">Minimum Password Length</Label>
                          <div className="flex items-center gap-4">
                            <Slider
                              id="password-length"
                              defaultValue={[editSettings.passwordPolicy.minLength]}
                              max={20}
                              min={6}
                              step={1}
                              onValueChange={(value) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    minLength: value[0],
                                  },
                                })
                              }
                              className="flex-1"
                            />
                            <span className="w-12 text-center">{editSettings.passwordPolicy.minLength}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="require-uppercase">Require Uppercase</Label>
                              <div className="text-sm text-muted-foreground">
                                Require at least one uppercase character
                              </div>
                            </div>
                            <Switch
                              id="require-uppercase"
                              checked={editSettings.passwordPolicy.requireUppercase}
                              onCheckedChange={(checked) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    requireUppercase: checked,
                                  },
                                })
                              }
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="require-lowercase">Require Lowercase</Label>
                              <div className="text-sm text-muted-foreground">
                                Require at least one lowercase character
                              </div>
                            </div>
                            <Switch
                              id="require-lowercase"
                              checked={editSettings.passwordPolicy.requireLowercase}
                              onCheckedChange={(checked) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    requireLowercase: checked,
                                  },
                                })
                              }
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="require-numbers">Require Numbers</Label>
                              <div className="text-sm text-muted-foreground">
                                Require at least one numeric character
                              </div>
                            </div>
                            <Switch
                              id="require-numbers"
                              checked={editSettings.passwordPolicy.requireNumbers}
                              onCheckedChange={(checked) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    requireNumbers: checked,
                                  },
                                })
                              }
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="require-special">Require Special Characters</Label>
                              <div className="text-sm text-muted-foreground">
                                Require at least one special character
                              </div>
                            </div>
                            <Switch
                              id="require-special"
                              checked={editSettings.passwordPolicy.requireSpecialChars}
                              onCheckedChange={(checked) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    requireSpecialChars: checked,
                                  },
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="password-expiry">Password Expiry (days)</Label>
                            <Input
                              id="password-expiry"
                              type="number"
                              value={editSettings.passwordPolicy.passwordExpiryDays}
                              onChange={(e) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    passwordExpiryDays: parseInt(e.target.value),
                                  },
                                })
                              }
                              min={0}
                              max={365}
                            />
                            <div className="text-xs text-muted-foreground">
                              Set to 0 to disable password expiry
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="prevent-reuse">Prevent Password Reuse</Label>
                              <div className="text-sm text-muted-foreground">
                                Prevent users from reusing previous passwords
                              </div>
                            </div>
                            <Switch
                              id="prevent-reuse"
                              checked={editSettings.passwordPolicy.preventPasswordReuse}
                              onCheckedChange={(checked) =>
                                setEditSettings({
                                  ...editSettings,
                                  passwordPolicy: {
                                    ...editSettings.passwordPolicy,
                                    preventPasswordReuse: checked,
                                  },
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Multi-Factor Authentication</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="mfa-enforced">Enforce MFA</Label>
                            <div className="text-sm text-muted-foreground">
                              Require all users to set up multi-factor authentication
                            </div>
                          </div>
                          <Switch
                            id="mfa-enforced"
                            checked={editSettings?.mfaSettings?.enforced || false}
                            onCheckedChange={(checked) =>
                              setEditSettings({
                                ...editSettings,
                                mfaSettings: {
                                  ...(editSettings?.mfaSettings || {}),
                                  enforced: checked,
                                },
                              })
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="allowed-methods">Allowed MFA Methods</Label>
                          <div className="flex flex-wrap gap-2">
                            <Badge
                              variant={editSettings.mfaSettings.allowedMethods.includes('totp') ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => {
                                const methods = [...editSettings.mfaSettings.allowedMethods];
                                if (methods.includes('totp')) {
                                  const index = methods.indexOf('totp');
                                  methods.splice(index, 1);
                                } else {
                                  methods.push('totp');
                                }
                                setEditSettings({
                                  ...editSettings,
                                  mfaSettings: {
                                    ...editSettings.mfaSettings,
                                    allowedMethods: methods,
                                  },
                                });
                              }}
                            >
                              Authenticator App
                            </Badge>
                            <Badge
                              variant={editSettings.mfaSettings.allowedMethods.includes('sms') ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => {
                                const methods = [...editSettings.mfaSettings.allowedMethods];
                                if (methods.includes('sms')) {
                                  const index = methods.indexOf('sms');
                                  methods.splice(index, 1);
                                } else {
                                  methods.push('sms');
                                }
                                setEditSettings({
                                  ...editSettings,
                                  mfaSettings: {
                                    ...editSettings.mfaSettings,
                                    allowedMethods: methods,
                                  },
                                });
                              }}
                            >
                              SMS
                            </Badge>
                            <Badge
                              variant={editSettings.mfaSettings.allowedMethods.includes('email') ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => {
                                const methods = [...editSettings.mfaSettings.allowedMethods];
                                if (methods.includes('email')) {
                                  const index = methods.indexOf('email');
                                  methods.splice(index, 1);
                                } else {
                                  methods.push('email');
                                }
                                setEditSettings({
                                  ...editSettings,
                                  mfaSettings: {
                                    ...editSettings.mfaSettings,
                                    allowedMethods: methods,
                                  },
                                });
                              }}
                            >
                              Email
                            </Badge>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="grace-logins">Grace Login Count</Label>
                          <Input
                            id="grace-logins"
                            type="number"
                            value={editSettings.mfaSettings.graceLoginCount}
                            onChange={(e) =>
                              setEditSettings({
                                ...editSettings,
                                mfaSettings: {
                                  ...editSettings.mfaSettings,
                                  graceLoginCount: parseInt(e.target.value),
                                },
                              })
                            }
                            min={0}
                            max={10}
                          />
                          <div className="text-xs text-muted-foreground">
                            Number of logins allowed before MFA is required
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Session Settings</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
                          <Input
                            id="session-timeout"
                            type="number"
                            value={editSettings.sessionSettings.sessionTimeout}
                            onChange={(e) =>
                              setEditSettings({
                                ...editSettings,
                                sessionSettings: {
                                  ...editSettings.sessionSettings,
                                  sessionTimeout: parseInt(e.target.value),
                                },
                              })
                            }
                            min={5}
                            max={1440}
                          />
                          <div className="text-xs text-muted-foreground">
                            Time after which users will be logged out
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="inactivity-timeout">Inactivity Timeout (minutes)</Label>
                          <Input
                            id="inactivity-timeout"
                            type="number"
                            value={editSettings.sessionSettings.inactivityTimeout}
                            onChange={(e) =>
                              setEditSettings({
                                ...editSettings,
                                sessionSettings: {
                                  ...editSettings.sessionSettings,
                                  inactivityTimeout: parseInt(e.target.value),
                                },
                              })
                            }
                            min={1}
                            max={60}
                          />
                          <div className="text-xs text-muted-foreground">
                            Time of inactivity before users are logged out
                          </div>
                        </div>

                        <div className="flex items-center justify-between md:col-span-2">
                          <div className="space-y-0.5">
                            <Label htmlFor="single-session">Enforce Single Session</Label>
                            <div className="text-sm text-muted-foreground">
                              Prevent users from having multiple active sessions
                            </div>
                          </div>
                          <Switch
                            id="single-session"
                            checked={editSettings.sessionSettings.enforceSingleSession}
                            onCheckedChange={(checked) =>
                              setEditSettings({
                                ...editSettings,
                                sessionSettings: {
                                  ...editSettings.sessionSettings,
                                  enforceSingleSession: checked,
                                },
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Login Protection</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="max-attempts">Max Login Attempts</Label>
                          <Input
                            id="max-attempts"
                            type="number"
                            value={editSettings.loginProtection.maxLoginAttempts}
                            onChange={(e) =>
                              setEditSettings({
                                ...editSettings,
                                loginProtection: {
                                  ...editSettings.loginProtection,
                                  maxLoginAttempts: parseInt(e.target.value),
                                },
                              })
                            }
                            min={1}
                            max={20}
                          />
                          <div className="text-xs text-muted-foreground">
                            Number of failed login attempts before account is locked
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="lockout-duration">Lockout Duration (minutes)</Label>
                          <Input
                            id="lockout-duration"
                            type="number"
                            value={editSettings.loginProtection.lockoutDuration}
                            onChange={(e) =>
                              setEditSettings({
                                ...editSettings,
                                loginProtection: {
                                  ...editSettings.loginProtection,
                                  lockoutDuration: parseInt(e.target.value),
                                },
                              })
                            }
                            min={1}
                            max={1440}
                          />
                          <div className="text-xs text-muted-foreground">
                            Time a user remains locked out after exceeding max attempts
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="ip-rate-limiting">IP Rate Limiting</Label>
                            <div className="text-sm text-muted-foreground">
                              Limit login attempts per IP address
                            </div>
                          </div>
                          <Switch
                            id="ip-rate-limiting"
                            checked={editSettings.loginProtection.ipRateLimiting}
                            onCheckedChange={(checked) =>
                              setEditSettings({
                                ...editSettings,
                                loginProtection: {
                                  ...editSettings.loginProtection,
                                  ipRateLimiting: checked,
                                },
                              })
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="require-captcha">Require CAPTCHA</Label>
                            <div className="text-sm text-muted-foreground">
                              Require CAPTCHA after failed login attempts
                            </div>
                          </div>
                          <Switch
                            id="require-captcha"
                            checked={editSettings.loginProtection.requireCaptcha}
                            onCheckedChange={(checked) =>
                              setEditSettings({
                                ...editSettings,
                                loginProtection: {
                                  ...editSettings.loginProtection,
                                  requireCaptcha: checked,
                                },
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setEditSettings(securitySettings)}
                  disabled={settingsLoading}
                >
                  Reset
                </Button>
                <Button
                  onClick={handleSettingsSubmit}
                  disabled={settingsLoading || updateSettingsMutation.isPending}
                >
                  {updateSettingsMutation.isPending && (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Changes
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Blocked Tab */}
          <TabsContent value="blocked" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Blocked IP Addresses</CardTitle>
                  <CardDescription>IP addresses blocked due to suspicious activity</CardDescription>
                </CardHeader>
                <CardContent>
                  {blockedIPsLoading ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : blockedIPs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Shield className="h-10 w-10 text-muted-foreground opacity-50 mb-2" />
                      <div className="text-muted-foreground">No blocked IP addresses</div>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <div className="overflow-x-auto">
                        <table className="w-full caption-bottom text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50 transition-colors hover:bg-slate-100">
                              <th className="h-10 px-4 text-left align-middle font-medium">IP Address</th>
                              <th className="h-10 px-4 text-left align-middle font-medium">Blocked At</th>
                              <th className="h-10 px-4 text-left align-middle font-medium">Duration</th>
                              <th className="h-10 px-4 text-left align-middle font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {blockedIPs.map((ip) => (
                              <tr key={ip.id} className="border-b transition-colors hover:bg-slate-100">
                                <td className="p-2 align-middle">
                                  <div className="font-medium">{ip.ipAddress}</div>
                                  <div className="text-xs text-muted-foreground truncate max-w-xs">{ip.reason}</div>
                                </td>
                                <td className="p-2 align-middle text-xs">
                                  {formatDate(ip.blockedAt)}
                                </td>
                                <td className="p-2 align-middle text-xs">
                                  {ip.blockedUntil ? `Until ${formatDate(ip.blockedUntil)}` : 'Permanent'}
                                </td>
                                <td className="p-2 align-middle">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => unblockIPMutation.mutate(ip.id)}
                                    disabled={unblockIPMutation.isPending}
                                  >
                                    <Shield className="h-4 w-4 mr-1" />
                                    Unblock
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Blocked Users</CardTitle>
                  <CardDescription>Users blocked due to suspicious activity</CardDescription>
                </CardHeader>
                <CardContent>
                  {blockedUsersLoading ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : blockedUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <User className="h-10 w-10 text-muted-foreground opacity-50 mb-2" />
                      <div className="text-muted-foreground">No blocked users</div>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <div className="overflow-x-auto">
                        <table className="w-full caption-bottom text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50 transition-colors hover:bg-slate-100">
                              <th className="h-10 px-4 text-left align-middle font-medium">User</th>
                              <th className="h-10 px-4 text-left align-middle font-medium">Blocked At</th>
                              <th className="h-10 px-4 text-left align-middle font-medium">Duration</th>
                              <th className="h-10 px-4 text-left align-middle font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {blockedUsers.map((user) => (
                              <tr key={user.id} className="border-b transition-colors hover:bg-slate-100">
                                <td className="p-2 align-middle">
                                  <div className="font-medium text-xs truncate max-w-[150px]">{user.email}</div>
                                  <div className="text-xs text-muted-foreground">{user.reason}</div>
                                </td>
                                <td className="p-2 align-middle text-xs">
                                  {formatDate(user.blockedAt)}
                                </td>
                                <td className="p-2 align-middle text-xs">
                                  {user.blockedUntil ? `Until ${formatDate(user.blockedUntil)}` : 'Permanent'}
                                </td>
                                <td className="p-2 align-middle">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => unblockUserMutation.mutate(user.id)}
                                    disabled={unblockUserMutation.isPending}
                                  >
                                    <User className="h-4 w-4 mr-1" />
                                    Unblock
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle>Security Events</CardTitle>
                  <CardDescription>Recent security-related events and alerts</CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search events..."
                    className="pl-8 w-full md:w-[250px]"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {eventsLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="h-10 w-10 text-muted-foreground opacity-50 mb-2" />
                    <div className="text-muted-foreground">
                      {searchQuery
                        ? `No results for "${searchQuery}". Try a different search term.`
                        : 'No security events found'}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <div className="overflow-x-auto">
                      <table className="w-full caption-bottom text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50 transition-colors hover:bg-slate-100">
                            <th className="h-10 px-4 text-left align-middle font-medium">Timestamp</th>
                            <th className="h-10 px-4 text-left align-middle font-medium">Event Type</th>
                            <th className="h-10 px-4 text-left align-middle font-medium">Severity</th>
                            <th className="h-10 px-4 text-left align-middle font-medium">User</th>
                            <th className="h-10 px-4 text-left align-middle font-medium">IP Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEvents.map((event) => [
                            <tr
                              key={`event-row-${event.id}`}
                              className={`border-b transition-colors hover:bg-slate-100 cursor-pointer ${
                                expandedEvent === event.id ? 'bg-slate-50' : ''
                              }`}
                              onClick={() => toggleEventExpanded(event.id)}
                            >
                              <td className="p-2 align-middle text-xs">
                                {formatDate(event.timestamp)}
                              </td>
                              <td className="p-2 align-middle">
                                {event.eventType}
                              </td>
                              <td className="p-2 align-middle">
                                <Badge className={getSeverityBadge(event.severity)}>
                                  {event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}
                                </Badge>
                              </td>
                              <td className="p-2 align-middle">
                                {event.username || 'N/A'}
                              </td>
                              <td className="p-2 align-middle">
                                {event.ipAddress}
                              </td>
                            </tr>,
                            expandedEvent === event.id ? (
                              <tr key={`event-details-${event.id}`} className="border-b bg-slate-50">
                                <td colSpan={5} className="p-4">
                                  <div className="text-sm">
                                    <div className="font-medium mb-1">Description:</div>
                                    <p className="mb-2">{event.description}</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
                                      <div>
                                        <span className="font-medium">User Agent:</span> {event.userAgent}
                                      </div>
                                      <div>
                                        <span className="font-medium">User ID:</span> {event.userId || 'N/A'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : null
                          ]).flat()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-between py-4">
                <div className="text-sm text-muted-foreground">
                  Showing {filteredEvents.length} of {securityEvents.length} events
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled>Previous</Button>
                  <Button variant="outline" size="sm" disabled>Next</Button>
                </div>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </SuperAdminLayout>
    </div>
  );
};

export default SecurityPage;